import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './HomePage.css';
import { Github, Linkedin } from 'lucide-react';
import Headshot from './Images/Miles Childress.jpg';
import { useIntersectionObserver } from './useIntersectionObserver';
import { projects } from './Portfolio';
import { WavyBackground } from './components/ui/wavy-background';

const waveColors = ["#38bdf8", "#818cf8", "#c084fc", "#e879f9"];

const experience = [
  {
    role: "Software Engineering Intern",
    company: "Staples (Quill)",
    location: "Lincolnshire, IL",
    period: "Jun 2025 – Aug 2025",
    bullets: [
      "Built a full-stack Power BI Report Portal (Next.js, TypeScript, Tailwind, Snowflake) with keyword-scored search, favorites, and dark mode — centralizing report discovery across the organization.",
      "Developed an enterprise Power BI dashboard with SQL views to auto-generate client slide decks in ~5 minutes, quantifying cost-savings opportunities using competitor spend data.",
      "Worked in SAFe/Agile standups, linked Bitbucket commits to Jira issues, and partnered with SRE on an Akamai 301 redirect rollout — gaining hands-on CI/CD and release management experience.",
    ],
  },
];

function HomePage({ darkMode }) {
  const [aboutRef, aboutInView] = useIntersectionObserver({ threshold: 0.1 });
  const [expRef, expInView] = useIntersectionObserver({ threshold: 0.08 });
  const [projectsRef, projectsInView] = useIntersectionObserver({ threshold: 0.1 });
  const [ctaRef, ctaInView] = useIntersectionObserver({ threshold: 0.1 });

  return (
    <div className="homepage-container overflow-x-hidden">

      {/* ── Hero ── */}
      <div className="hero-section-wrapper relative h-[calc(100vh-80px)] min-h-[500px] w-full">
        <WavyBackground
          backgroundFill="#080c12"
          colors={waveColors}
          waveWidth={50}
          blur={10}
          speed="fast"
          waveOpacity={0.45}
          containerClassName="absolute inset-0"
          className="flex flex-col items-center justify-center text-center z-10 px-4"
        >
          <div className="hero-content relative z-10 max-w-4xl mx-auto">

            <motion.h1
              className="text-6xl md:text-8xl lg:text-9xl font-bold text-white mb-5 leading-none tracking-tight"
              style={{ fontFamily: 'Clash Display, sans-serif', letterSpacing: '-2px' }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Miles Childress
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-gray-400 mb-10 max-w-xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              Texas A&M University · Mays Business School · Class of 2026
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Link
                to="/portfolio"
                className="px-8 py-3.5 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-colors text-sm tracking-wide w-full sm:w-auto text-center"
              >
                View My Work
              </Link>
              <a
                href={`${process.env.PUBLIC_URL}/Miles Childress MIS RESUME 2025.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                download="Miles_Childress_Resume.pdf"
                className="px-8 py-3.5 bg-transparent border border-white/25 text-white font-semibold rounded-lg hover:bg-white/8 hover:border-white/40 transition-all text-sm tracking-wide w-full sm:w-auto text-center"
              >
                Download Resume
              </a>
            </motion.div>

          </div>
        </WavyBackground>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M12 19L19 12M12 19L5 12" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.div>
        </div>
      </div>

      {/* ── About Snapshot ── */}
      <section className="py-20 bg-[#080c12]" ref={aboutRef}>
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <motion.div
            className="flex flex-col md:flex-row items-center gap-10 md:gap-16"
            initial={{ opacity: 0, y: 30 }}
            animate={aboutInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="flex-shrink-0">
              <div className="w-44 h-44 md:w-60 md:h-60 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl mx-auto">
                <img src={Headshot} alt="Miles Childress" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                About Me
              </h2>
              <p className="text-gray-400 text-base md:text-lg mb-5 leading-relaxed max-w-2xl">
                Senior MIS student at Texas A&M's Mays Business School, specializing in cloud technologies, full-stack web development, and systems analysis. I bring hands-on experience building and deploying production applications, managing secure infrastructure, and translating business problems into technical solutions.
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start mb-7">
                <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-md text-xs text-gray-400 font-medium">Texas A&M · GPA 3.3</span>
                <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-md text-xs text-gray-400 font-medium">AWS Certified Cloud Practitioner</span>
                <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-md text-xs text-gray-400 font-medium">Graduating May 2026</span>
              </div>
              <Link to="/about" className="inline-block px-6 py-2.5 bg-white/8 border border-white/15 hover:bg-white/12 hover:border-white/25 text-white font-medium rounded-lg transition-all text-sm">
                Full Profile →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Experience ── */}
      <section className="py-20 bg-[#0d1117]" ref={expRef}>
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-white mb-12"
            style={{ fontFamily: 'Clash Display, sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            animate={expInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            Experience
          </motion.h2>

          {experience.map((job, i) => (
            <motion.div
              key={i}
              className="flex flex-col md:flex-row gap-6 md:gap-12 pb-10 border-b border-white/6 last:border-0 last:pb-0"
              initial={{ opacity: 0, y: 24 }}
              animate={expInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              {/* Left column */}
              <div className="md:w-56 flex-shrink-0">
                <p className="text-xs font-semibold tracking-widest uppercase text-blue-400 mb-1">{job.period}</p>
                <h3 className="text-white font-bold text-lg leading-tight mb-1" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                  {job.role}
                </h3>
                <p className="text-gray-500 text-sm">{job.company}</p>
                <p className="text-gray-600 text-xs mt-0.5">{job.location}</p>
              </div>

              {/* Right column */}
              <div className="flex-1">
                <ul className="space-y-3">
                  {job.bullets.map((b, j) => (
                    <li key={j} className="flex gap-3 text-gray-400 text-sm leading-relaxed">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-500/70 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}

          {/* Case Competition callout */}
          <motion.div
            className="mt-10 flex flex-col md:flex-row gap-6 md:gap-12"
            initial={{ opacity: 0, y: 24 }}
            animate={expInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="md:w-56 flex-shrink-0">
              <p className="text-xs font-semibold tracking-widest uppercase text-blue-400 mb-1">Nov 2024</p>
              <h3 className="text-white font-bold text-lg leading-tight mb-1" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                Championship Finalist
              </h3>
              <p className="text-gray-500 text-sm">CMIS Case Competition</p>
              <p className="text-gray-600 text-xs mt-0.5">Texas A&M University</p>
            </div>
            <div className="flex-1">
              <ul className="space-y-3">
                <li className="flex gap-3 text-gray-400 text-sm leading-relaxed">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-500/70 flex-shrink-0" />
                  Built a fintech solution for unbanked users with a cross-functional team; led market analysis and strategy evaluation, delivered a 20-minute pitch to industry judges, and placed 5th out of 60 teams.
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Featured Projects ── */}
      <section className="py-20 bg-[#080c12]" ref={projectsRef}>
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-white mb-12"
            style={{ fontFamily: 'Clash Display, sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            animate={projectsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            Featured Projects
          </motion.h2>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            initial="hidden"
            animate={projectsInView ? "visible" : "hidden"}
          >
            {projects.slice(0, 3).map((project, index) => (
              <motion.div
                key={index}
                className="bg-[#111620] border border-[#1e2a3a] rounded-xl overflow-hidden hover:-translate-y-1.5 transition-all duration-300 shadow-xl"
                variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } }}
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="text-base font-bold mb-2 text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>{project.title}</h3>
                  <p className="text-gray-500 mb-5 text-sm leading-relaxed line-clamp-3">{project.description}</p>
                  <div className="mt-auto">
                    {project.private ? (
                      <div className="w-full text-center py-2 bg-white/3 text-gray-600 rounded-lg border border-white/6 text-xs flex items-center justify-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                        Private Repository
                      </div>
                    ) : (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center py-2 bg-white/6 hover:bg-white/10 text-white rounded-lg transition-colors text-xs font-medium"
                      >
                        View Project →
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          <div className="mt-10">
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium transition-colors">
              View All Projects
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Connect ── */}
      <section className="py-20 bg-[#0d1117]" ref={ctaRef}>
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-bold mb-3 text-white"
            style={{ fontFamily: 'Clash Display, sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            Let's Connect
          </motion.h2>
          <motion.p
            className="text-gray-500 mb-10 text-base"
            initial={{ opacity: 0, y: 16 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Open to full-time Software Engineering and Business Technology roles starting May 2026.
          </motion.p>

          <div className="flex flex-col md:flex-row justify-center gap-4 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 bg-[#111620] border border-[#1e2a3a] p-7 rounded-xl hover:-translate-y-1 transition-all duration-300"
            >
              <a href="https://github.com/mileschildress88" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center">
                <Github size={36} className="text-white mb-3" />
                <h3 className="text-base font-bold text-white mb-1" style={{ fontFamily: 'Clash Display, sans-serif' }}>GitHub</h3>
                <p className="text-gray-500 text-sm mb-4">@mileschildress88</p>
                <span className="px-5 py-2 bg-white/6 rounded-md text-white text-xs font-semibold hover:bg-white/10 transition-colors">View Profile</span>
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex-1 bg-[#111620] border border-[#1e2a3a] p-7 rounded-xl hover:-translate-y-1 transition-all duration-300"
            >
              <a href="https://www.linkedin.com/in/jmiles-childress" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center">
                <Linkedin size={36} className="text-blue-500 mb-3" />
                <h3 className="text-base font-bold text-white mb-1" style={{ fontFamily: 'Clash Display, sans-serif' }}>LinkedIn</h3>
                <p className="text-gray-500 text-sm mb-4">jmiles-childress</p>
                <span className="px-5 py-2 bg-blue-600 rounded-md text-white text-xs font-semibold hover:bg-blue-700 transition-colors">Connect</span>
              </a>
            </motion.div>
          </div>

          <motion.div
            className="bg-[#111620] border border-[#1e2a3a] p-8 rounded-xl"
            initial={{ opacity: 0, y: 24 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <p className="text-gray-400 text-base mb-5">Have a question or want to work together?</p>
            <Link to="/contact" className="inline-block px-10 py-3.5 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-colors text-sm tracking-wide shadow-lg">
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  );
}

export default HomePage;
